/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon } from 'semantic-ui-react';
import { gotoFn } from '../../helpers/routing';

import { swallowError } from '../../helpers/utils';
import { isStoreError, isStoreLoading, isStoreReady } from '../../models/BaseStore';
import BasicProgressPlaceholder from '../helpers/BasicProgressPlaceholder';
import ErrorBox from '../helpers/ErrorBox';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// -  authenticationProviderConfigsStore (via injection)
// -  embedded (optional) - when true, renders without Container wrapper
class AuthenticationProvidersList extends Component {
  componentDidMount() {
    const store = this.getStore();
    swallowError(store.load());
  }

  getStore() {
    return this.props.authenticationProviderConfigsStore;
  }

  handleEditClick = (id) => {
    const goto = gotoFn(this);
    goto(`/authentication-providers/${encodeURIComponent(id)}/edit`);
  };

  render() {
    const store = this.getStore();
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} />;
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder segmentCount={2} />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    }

    const inner = (
      <div className="mt3 animated fadeIn">
        {this.renderHeader()}
        {content}
      </div>
    );

    if (this.props.embedded) return inner;
    return <Container>{inner}</Container>;
  }

  renderHeader() {
    const store = this.getStore();
    const count = isStoreReady(store) ? store.list.length : null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: 600, color: 'var(--rmit-blue)' }}>
          <Icon name="shield alternate" style={{ ...iconStyle, marginRight: '8px' }} />
          Authentication Providers
        </h3>
        {count !== null && (
          <span className="workspace-status-badge" style={{ background: '#f3f4f6', color: '#374151', fontSize: '0.75em' }}>
            {count} total
          </span>
        )}
      </div>
    );
  }

  renderMain() {
    const store = this.getStore();
    const list = store.list;

    if (list.length === 0) {
      return (
        <div className="workspace-empty-state" style={{ marginTop: '24px' }}>
          <div className="workspace-empty-state-icon"><Icon name="shield alternate" /></div>
          <div className="workspace-empty-state-title">No authentication providers</div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {_.map(list, (provider) => this.renderProviderCard(provider))}
      </div>
    );
  }

  renderProviderCard(provider) {
    const isActive = _.toLower(provider.status) === 'active';
    const title = _.get(provider, 'config.title', provider.id);
    const description = _.get(provider, 'config.type.description', '');

    return (
      <div key={provider.id} className="workspace-card" style={{ overflow: 'hidden' }}>
        <div className="workspace-card-accent" style={{ background: isActive ? 'var(--rmit-blue)' : '#dc2626' }} />
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <span style={{ fontSize: '1.05em', fontWeight: 600, color: '#111827' }}>{title}</span>
                <span
                  className="workspace-status-badge"
                  style={isActive
                    ? { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
                    : { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
                  }
                >
                  {provider.status}
                </span>
              </div>
              <div style={{ fontSize: '0.8em', color: '#9ca3af', fontFamily: 'monospace', marginBottom: '6px', wordBreak: 'break-all' }}>
                {provider.id}
              </div>
              {description && (
                <div style={{ fontSize: '0.88em', color: '#6b7280', lineHeight: 1.5 }}>{description}</div>
              )}
            </div>
            <button
              className="workspace-action-btn workspace-action-btn-secondary"
              onClick={() => this.handleEditClick(provider.id)}
              style={{ flexShrink: 0 }}
            >
              <Icon name="pencil" style={iconStyle} /> Edit
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default inject('authenticationProviderConfigsStore')(withRouter(observer(AuthenticationProvidersList)));
